import {useMutation, useQuery} from "@tanstack/react-query";
import {useEffect, useMemo, useState} from "react";
import {AccountAvatar} from "@/components/AccountAvatar";
import {useSearchParams} from "react-router-dom";
import {getMe} from "@/api/auth";
import {createCheckout, getSubscription, listPlans, listSubscriptionPayments, type PlanPriceInfo,} from "@/api/billing";
import {forgetPayment, readPendingPayment, rememberPayment, trackFunnel} from "@/lib/funnel";
import {ApiError} from "@/api/client";
import {bindReferralCode} from "@/api/referrals";
import {useAuthBootstrap} from "@/hooks/useAuthBootstrap";
import {formatReceiptDestination, hasCompleteReceiptContact, validateCheckoutContact,} from "@/lib/receiptContact";
import {
    billingPeriodChargeFrequency,
    formatMinor,
    hasPaidAccess,
    listSubscriptionOffers,
    type PaywallMode,
    resolveChargeAmountMinor,
    resolveMonthlyPrice,
    resolvePaywallMode,
    type SubscriptionOffer,
} from "@/lib/subscription";
import {type AuthUser, useLandingAuth} from "@/state/authStore";
import {openAccountModal} from "@/accountModal";

const MINI_APP_URL =
  (import.meta.env.VITE_MINI_APP_URL as string | undefined) ??
  "https://t.me/leadhold_bot/leadhold";

const SUBSCRIBE_DRAFT_KEY = "leadhold_subscribe_draft";

type MessageKind = "muted" | "ok" | "error";

interface SubscribeDraft {
  refCode?: string;
  customerEmail?: string;
  customerPhone?: string;
}

function payButtonText(
  offer: SubscriptionOffer,
  mode: PaywallMode,
): string {
  const amount = offer.trial
    ? offer.price.amount_minor
    : resolveChargeAmountMinor(offer.price, mode);
  return `Оплатить ${formatMinor(amount, offer.price.currency)}`;
}

function consentPrimaryText(
  offer: SubscriptionOffer,
  mode: PaywallMode,
  monthlyPrice?: PlanPriceInfo,
): string {
  if (offer.trial) {
    const trialPrice = formatMinor(offer.price.amount_minor, offer.price.currency);
    const renewalPrice = monthlyPrice
      ? formatMinor(resolveChargeAmountMinor(monthlyPrice, mode), monthlyPrice.currency)
      : "—";
    return `Сейчас спишется ${trialPrice} за пробный доступ. Затем подписка продлится за ${renewalPrice} ежемесячно. Отключить автопродление можно в личном кабинете в разделе «Оплата».`;
  }
  const amount = formatMinor(
    resolveChargeAmountMinor(offer.price, mode),
    offer.price.currency,
  );
  const frequency = billingPeriodChargeFrequency(offer.price.billing_period);
  return `Оплачивая подписку, вы соглашаетесь на списание ${amount} ${frequency}. Отключить автопродление можно в личном кабинете в разделе «Оплата».`;
}

function parseSubscribeRefCode(searchParams: URLSearchParams): string | null {
  const ref = searchParams.get("ref")?.trim();
  if (!ref) {
    return null;
  }
  const code = ref.toLowerCase().replace(/^ref_/, "");
  return /^[a-z0-9]{8}$/.test(code) ? code : null;
}

function initialSelectedPriceId(
  searchParams: URLSearchParams,
  offers: SubscriptionOffer[],
): string | null {
  const fromQuery = searchParams.get("plan_price_id");
  if (fromQuery && offers.some((offer) => offer.price.id === fromQuery)) {
    return fromQuery;
  }
  return offers.find((offer) => offer.trial)?.price.id
    ?? offers.find((offer) => offer.price.billing_period === "MONTHLY")?.price.id
    ?? offers[0]?.price.id
    ?? null;
}

function modeContent(mode: PaywallMode, price: PlanPriceInfo) {
  const base = formatMinor(price.amount_minor, price.currency);
  const referral = formatMinor(price.referral_amount_minor, price.currency);
  const leader = formatMinor(price.leader_amount_minor, price.currency);
  const referralDiscountMinor = Math.max(
    0,
    price.amount_minor - price.referral_amount_minor,
  );
  const referralDiscount =
    referralDiscountMinor > 0
      ? formatMinor(referralDiscountMinor, price.currency)
      : null;

  if (mode === "leader") {
    return {
      label: "Лидерский доступ",
      price: leader,
      note: "После оплаты откроется двухуровневая реферальная программа.",
      programTitle: "Реферальная программа",
      programText:
        "Лидер получает 15% с оплат пользователей первого уровня и 10% с оплат второго уровня.",
    };
  }

  if (mode === "referral") {
    return {
      label: "По реферальному коду",
      price: referral,
      note: referralDiscount
        ? `Код применён: скидка ${referralDiscount} уже учтена в сумме оплаты.`
        : "Код применён: цена со скидкой уже учтена в сумме оплаты.",
      programTitle: "Реферальная программа: 20%",
      programText:
        "После оплаты вы получите личный код и сможете получать 20% с оплат приглашённых пользователей.",
    };
  }

  return {
    label: "Подписка Лидхолд",
    price: base,
    note: "Полный доступ к сервису для холодных продаж в Telegram.",
    programTitle: "Реферальная программа: 20%",
    programText:
      "После оплаты вы получите личный код и сможете приглашать пользователей со скидкой.",
  };
}

function userDisplayName(user: AuthUser): string {
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ");
  if (fullName.trim()) {
    return fullName.trim();
  }
  if (user.telegram_username) {
    return `@${user.telegram_username}`;
  }
  return "Telegram подключён";
}

type CheckoutOption = "monthly" | "trial";

export function SubscribePage({checkoutOption}: {checkoutOption?: CheckoutOption} = {}) {
  const [searchParams] = useSearchParams();
  const returningFromPayment = searchParams.get("payment") === "return";
  const [pendingPayment] = useState(readPendingPayment);
  const [paymentReported, setPaymentReported] = useState(false);
  const [paymentPollStarted] = useState(Date.now);
  const user = useLandingAuth((s) => s.user);
  const { status: authStatus, error: authError } = useAuthBootstrap();
  const [refCode, setRefCode] = useState("");
  const [refMessage, setRefMessage] = useState("");
  const [refMessageKind, setRefMessageKind] = useState<MessageKind>("muted");
  const [checkoutErr, setCheckoutErr] = useState<string | null>(null);
  const [contactInvalid, setContactInvalid] = useState(false);
  const [contactFieldError, setContactFieldError] = useState<
    "missing" | "missing_email" | "missing_phone" | "email" | "phone" | null
  >(null);
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");

  useEffect(() => {
    const code = parseSubscribeRefCode(searchParams);
    if (code) {
      window.location.replace(`/invite/${code}`);
    }
  }, [searchParams]);

  useEffect(() => {
    const raw = sessionStorage.getItem(SUBSCRIBE_DRAFT_KEY);
    if (!raw) {
      return;
    }
    sessionStorage.removeItem(SUBSCRIBE_DRAFT_KEY);

    try {
      const draft = JSON.parse(raw) as SubscribeDraft;
      const nextRefCode = draft.refCode?.trim() ?? "";

      setRefCode(nextRefCode);
      setCustomerEmail(draft.customerEmail ?? "");
      setCustomerPhone(draft.customerPhone ?? "");

      if (nextRefCode) {
        setRefMessage("Нажмите «Применить», чтобы проверить код.");
        setRefMessageKind("muted");
      }
    } catch {
      sessionStorage.removeItem(SUBSCRIBE_DRAFT_KEY);
    }
  }, []);

  const subscription = useQuery({
    queryKey: ["subscription"],
    queryFn: getSubscription,
    enabled: authStatus === "authenticated",
    refetchInterval: returningFromPayment ? 2000 : false,
  });

  const plans = useQuery({
    queryKey: ["plans"],
    queryFn: listPlans,
  });

  const confirmedPayments = useQuery({
    queryKey: ["landing-payment-confirmation", pendingPayment?.paymentId],
    queryFn: () => listSubscriptionPayments(100),
    enabled: returningFromPayment && authStatus === "authenticated" && Boolean(pendingPayment) && !paymentReported,
    refetchInterval: () => Date.now() - paymentPollStarted < 60000 ? 2000 : false,
  });

  useEffect(() => {
    if (!pendingPayment || !confirmedPayments.data || paymentReported) return;
    const payment = confirmedPayments.data.payments.find((item) => item.id === pendingPayment.paymentId);
    if (!payment || payment.status === "PENDING") return;
    if (payment.status === "SUCCEEDED") {
      trackFunnel("payment_success", {
        checkout_option: pendingPayment.option,
        plan: pendingPayment.option === "trial" ? "trial" : "standard",
        source: "subscribe",
      }, `payment:${payment.id}`);
    }
    forgetPayment();
    setPaymentReported(true);
  }, [confirmedPayments.data, pendingPayment, paymentReported]);


  const monthlyPrice = useMemo(
    () => resolveMonthlyPrice(plans.data?.plans),
    [plans.data?.plans],
  );
  const trialEligible = subscription.data?.trial_eligible ?? false;
  const offers = useMemo(() => {
    const allowTrialBeforeAuthentication =
      checkoutOption === "trial" && authStatus !== "authenticated";
    return listSubscriptionOffers(
      plans.data?.plans,
      trialEligible || allowTrialBeforeAuthentication,
    ).filter((offer) => {
      if (checkoutOption === "trial") return offer.trial;
      if (checkoutOption === "monthly") return offer.price.billing_period === "MONTHLY";
      return offer.trial || offer.price.billing_period === "MONTHLY";
    });
  }, [plans.data?.plans, trialEligible, authStatus, checkoutOption]);
  const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null);

  useEffect(() => {
    if (authStatus === "authenticated" && !subscription.isSuccess) {
      return;
    }
    setSelectedPriceId((current) => {
      const requested = searchParams.get("plan_price_id");
      if (requested && offers.some((offer) => offer.price.id === requested)) {
        return requested;
      }
      if (current && offers.some((offer) => offer.price.id === current)) {
        return current;
      }
      return initialSelectedPriceId(searchParams, offers);
    });
  }, [authStatus, offers, searchParams, subscription.isSuccess]);
  const selectedOffer = offers.find((offer) => offer.price.id === selectedPriceId);
  const inviterName = user?.referrer_display_name?.trim() || null;
  const hasBoundReferral = Boolean(user?.has_referral_binding);
  const showInviterBlock = Boolean(inviterName);
  const isLeader = user?.role === "LEADER";
  const mode = resolvePaywallMode(isLeader, hasBoundReferral);
  const contentPrice = selectedOffer?.trial ? monthlyPrice : selectedOffer?.price;
  const content = contentPrice ? modeContent(mode, contentPrice) : null;
  const appliedReferralDiscountMinor =
    selectedOffer && !selectedOffer.trial && mode === "referral"
      ? Math.max(
          0,
          selectedOffer.price.amount_minor - selectedOffer.price.referral_amount_minor,
        )
      : 0;
  const storedEmail = user?.email ?? null;
  const storedPhone = user?.phone_number ?? null;
  const contactReady = hasCompleteReceiptContact(storedEmail, storedPhone);
  const receiptDestination = formatReceiptDestination(storedEmail, storedPhone);
  const isAuthenticated = authStatus === "authenticated" && Boolean(user);

  useEffect(() => {
    setCustomerEmail((current) => current || storedEmail || "");
    setCustomerPhone((current) => current || storedPhone || "");
  }, [storedEmail, storedPhone]);

  const checkout = useMutation({
    mutationFn: createCheckout,
    onSuccess: (response) => {
      const option = selectedOffer?.trial ? "trial" : "monthly";
      rememberPayment(response.payment_id, option);
      trackFunnel("payment_start", {checkout_option: option, plan: option === "trial" ? "trial" : "standard", source: checkoutOption ? "pricing" : "subscribe"});
      window.location.href = response.checkout_url;
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        if (error.code === "checkout_contact_required") {
          setCheckoutErr("Укажите email и телефон для чека.");
          return;
        }
        if (error.code === "checkout_email_invalid") {
          setCheckoutErr("Некорректный email.");
          return;
        }
        if (error.code === "checkout_phone_invalid") {
          setCheckoutErr("Некорректный телефон. Пример: +79001234567");
          return;
        }
        if (error.code === "trial_not_eligible") {
          setCheckoutErr("Trial доступен только для новых пользователей.");
          setSelectedPriceId(
            offers.find((offer) => offer.price.billing_period === "MONTHLY")?.price.id
              ?? offers.find((offer) => !offer.trial)?.price.id
              ?? null,
          );
          return;
        }
        if (error.code === "subscription_active") {
          setCheckoutErr("У вас уже есть активная подписка.");
          return;
        }
      }
      setCheckoutErr("Не удалось открыть оплату. Попробуйте ещё раз.");
    },
  });

  const saveCheckoutDraftForLogin = () => {
    const draft: SubscribeDraft = {
      refCode: refCode || parseSubscribeRefCode(searchParams) || undefined,
      customerEmail,
      customerPhone,
    };

    sessionStorage.setItem(SUBSCRIBE_DRAFT_KEY, JSON.stringify(draft));
  };

  const requestRegistration = () => {
    saveCheckoutDraftForLogin();
    const activeElement = document.activeElement;
    void openAccountModal("register", activeElement instanceof HTMLElement ? activeElement : null);
  };

  useEffect(() => {
    if (authStatus !== "unauthenticated" || checkoutOption) {
      return;
    }
    const code = parseSubscribeRefCode(searchParams);
    if (code) {
      window.location.replace(`/invite/${code}`);
      return;
    }
    requestRegistration();
  }, [authStatus, searchParams, checkoutOption]);

  const bindReferral = useMutation({
    mutationFn: bindReferralCode,
    onSuccess: async () => {
      await getMe();
      setRefCode("");
      setRefMessage("");
    },
    onError: async (error) => {
      if (error instanceof ApiError) {
        if (error.code === "referral_already_bound") {
          await getMe();
          setRefCode("");
          setRefMessageKind("ok");
          setRefMessage("Промокод уже применён к вашему аккаунту.");
          return;
        }
        if (error.code === "referral_code_not_found") {
          setRefMessageKind("error");
          setRefMessage("Код не найден. Проверьте и попробуйте снова.");
          return;
        }
        if (error.code === "self_referral_not_allowed") {
          setRefMessageKind("error");
          setRefMessage("Нельзя использовать свой код.");
          return;
        }
        if (error.code === "referral_code_expired") {
          setRefMessageKind("error");
          setRefMessage("Код лидера недоступен. Запросите новый у партнёра.");
          return;
        }
        if (error.code === "referral_code_exhausted") {
          setRefMessageKind("error");
          setRefMessage("Код лидера недоступен. Запросите новый у партнёра.");
          return;
        }
        if (error.code === "referral_bind_after_subscription") {
          setRefMessageKind("error");
          setRefMessage("Промокод можно применить только до первой оплаты.");
          return;
        }
      }
      setRefMessageKind("error");
      setRefMessage("Не удалось применить код. Попробуйте ещё раз.");
    },
  });

  const applyReferralCode = () => {
    const code = refCode.trim();
    setCheckoutErr(null);

    if (!code || hasBoundReferral) {
      return;
    }

    if (authStatus !== "authenticated") {
      requestRegistration();
      return;
    }

    bindReferral.mutate(code);
  };

  const startCheckout = () => {
    setCheckoutErr(null);
    setContactFieldError(null);

    if (authStatus !== "authenticated" || !user) {
      requestRegistration();
      return;
    }

    const validation = validateCheckoutContact(
      storedEmail,
      storedPhone,
      customerEmail,
      customerPhone,
    );
    if (!validation.ok) {
      setContactInvalid(true);
      setContactFieldError(validation.error);
      setCheckoutErr(null);
      return;
    }

    const checkoutPrice = selectedOffer?.price;
    if (!checkoutPrice) {
      setCheckoutErr("Не удалось получить тариф. Обновите страницу.");
      return;
    }

    checkout.mutate({
      plan_price_id: checkoutPrice.id,
      ...validation.contact,
    });
  };

  const contactHint =
    contactFieldError === "email"
      ? "Некорректный email."
      : contactFieldError === "phone"
        ? "Некорректный телефон. Пример: +79001234567"
        : contactFieldError === "missing_email"
          ? "Укажите email для чека."
          : contactFieldError === "missing_phone"
            ? "Укажите телефон для чека."
            : contactInvalid
              ? "Укажите email и телефон, чтобы продолжить оплату."
              : contactReady
                ? "Email и телефон для чека можно изменить перед оплатой."
                : "Для чека укажите email и телефон.";

  const subStatus = subscription.data?.subscription?.status ?? "NONE";
  const canEditReferral =
    subscription.isSuccess && (trialEligible || subStatus === "NONE");
  const priceLoading = plans.isLoading || subscription.isLoading;
  const checkoutPrice = selectedOffer?.price;
  const payDisabled =
    checkout.isPending || priceLoading || !checkoutPrice || authStatus === "loading";

  if (authStatus === "authenticated" && hasPaidAccess(subStatus)) {
    return (
      <main className="subscribe-screen subscribe-screen-centered">
        <section className="active-card">
          <p className="kicker">Подписка активна</p>
          <h1>Доступ к Лидхолд открыт</h1>
          <p>
            Оплата уже подтверждена. Можно вернуться в приложение и продолжить
            работу.
          </p>
          <a className="pay" href={MINI_APP_URL}>
            Открыть приложение
          </a>
          <a className="active-secondary" href="/cabinet/">
            Личный кабинет
          </a>
          <a className="active-secondary" href="/">
            Вернуться на сайт
          </a>
        </section>
      </main>
    );
  }

  return (
    <main className="subscribe-screen">
      <section className="subscribe-main" aria-label="Что входит в подписку">
        <div className="main-inner">
          <div className="kicker">Подписка Лидхолд</div>
          <h1>
            Оформите доступ <em>к Лидхолд</em>
          </h1>
          <p className="lead">
            Полный доступ к сервису для холодных продаж в Telegram: подключение
            аккаунтов, парсинг аудитории, рассылки, CRM для лидов и AI-перефраз
            сообщений.
          </p>
          <div className="features">
            <div className="feature">
              <strong>До 20 Telegram-аккаунтов</strong>
              <span>Подключайте рабочие аккаунты и распределяйте нагрузку.</span>
            </div>
            <div className="feature">
              <strong>Парсинг аудитории</strong>
              <span>Сбор username из публичных Telegram-групп без дублей.</span>
            </div>
            <div className="feature">
              <strong>Рассылки по базе</strong>
              <span>Кампании по собранным контактам с контролем темпа.</span>
            </div>
            <div className="feature">
              <strong>CRM для лидов</strong>
              <span>Статусы, теги, заметки и ответы в одной воронке.</span>
            </div>
          </div>
        </div>
      </section>

      <aside className="subscribe-side" aria-label="Оплата подписки">
        <a className="close" href="/" aria-label="Вернуться на сайт">
          ×
        </a>
        <div className="payment">
          <div
            className={
              user ? "account" : "account"
            }
          >
            {authStatus === "loading" ? (
              <span>Проверяем Telegram-аккаунт...</span>
            ) : user ? (
              <>
                <AccountAvatar user={user} />
                <span>{userDisplayName(user)}</span>
              </>
            ) : (
              <span>Telegram подключим перед оплатой</span>
            )}
          </div>

          {returningFromPayment ? (
            <p className="notice">Ожидаем подтверждение оплаты...</p>
          ) : null}
          {authStatus === "error" && authError ? (
            <p className="error-text">{authError}</p>
          ) : null}

          {priceLoading ? (
            <p className="notice">Загружаем тариф...</p>
          ) : (
            <div className="plan-picker">
              {offers.map((offer) => {
                const amount = offer.trial
                  ? offer.price.amount_minor
                  : resolveChargeAmountMinor(offer.price, mode);
                return (
                  <button
                    key={offer.price.id}
                    type="button"
                    className={`plan-option${selectedPriceId === offer.price.id ? " is-selected" : ""}`}
                    onClick={() => setSelectedPriceId(offer.price.id)}
                  >
                    <span className="plan-option-label">
                      {offer.trial ? "Trial" : content?.label ?? "Подписка Лидхолд"}
                    </span>
                    <strong>
                      {formatMinor(amount, offer.price.currency)}
                    </strong>
                    <span className="plan-option-meta">
                      {offer.trial
                        ? `${offer.plan.trial_days ?? 3} дня · до 3 аккаунтов`
                        : "/ месяц · до 20 аккаунтов"}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {isAuthenticated && selectedOffer && appliedReferralDiscountMinor > 0 ? (
            <div className="referral-discount" role="status">
              <span>Партнёрский код применён</span>
              <strong>
                −{formatMinor(appliedReferralDiscountMinor, selectedOffer.price.currency)}
              </strong>
            </div>
          ) : null}

          {checkoutOption === "trial" && authStatus === "authenticated"
          && subscription.isSuccess && !trialEligible ? (
            <p className="notice">Пробный доступ доступен только новым пользователям. <a href="/pricing.html?checkout=monthly">Посмотреть месячный тариф</a> или <a href="/cabinet/">открыть личный кабинет</a>.</p>
          ) : null}

          {isAuthenticated && selectedOffer ? (
            <div className="program consent-block">
              <p>{consentPrimaryText(selectedOffer, mode, monthlyPrice)}</p>
            </div>
          ) : null}

          <button
            className="pay"
            type="button"
            disabled={payDisabled}
            onClick={startCheckout}
          >
            {checkout.isPending
              ? "Создаём оплату..."
              : priceLoading
                ? "Загружаем тариф..."
                : !isAuthenticated
                  ? "Подключить Telegram"
                  : selectedOffer
                    ? payButtonText(selectedOffer, mode)
                    : "Оплатить"}
          </button>

          {!isAuthenticated && authStatus !== "loading" ? (
            <p className="notice">
              Сначала подключите Telegram-аккаунт. После этого откроем промокод и поля для чека.
            </p>
          ) : null}

          {isAuthenticated && canEditReferral ? (
            showInviterBlock ? (
              <p className="inviter status-ok">
                Вас пригласил: <strong>{inviterName}</strong>
              </p>
            ) : !hasBoundReferral ? (
              <div className="promo">
                <label htmlFor="ref-code">Промокод</label>
                <div className="promo-row">
                  <input
                    id="ref-code"
                    value={refCode}
                    onChange={(event) => {
                      setRefCode(event.target.value);
                      if (!event.target.value.trim()) {
                        setRefMessage("");
                        setRefMessageKind("muted");
                      } else if (refMessageKind === "error") {
                        setRefMessage("");
                        setRefMessageKind("muted");
                      }
                    }}
                    placeholder="Введите код"
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    disabled={!refCode.trim() || bindReferral.isPending}
                    onClick={applyReferralCode}
                  >
                    {bindReferral.isPending ? "Проверяем..." : "Применить"}
                  </button>
                </div>
                {refMessage ? (
                  <p className={`status status-${refMessageKind}`}>{refMessage}</p>
                ) : null}
              </div>
            ) : null
          ) : null}

          {content ? (
            <div className="program">
              <strong>{content.programTitle}</strong>
              <p>{content.programText}</p>
            </div>
          ) : null}

          {isAuthenticated && receiptDestination ? (
            <p className="notice">{receiptDestination}</p>
          ) : null}

          {isAuthenticated ? (
            <div className={`contact-panel${contactInvalid ? " is-invalid" : ""}`}>
              <p>{contactHint}</p>
              <input
                value={customerEmail}
                onChange={(event) => {
                  setCustomerEmail(event.target.value);
                  setContactInvalid(false);
                  setContactFieldError(null);
                }}
                placeholder="you@example.com"
                type="email"
                autoComplete="email"
                aria-invalid={
                  contactFieldError === "email" ||
                  contactFieldError === "missing_email" ||
                  contactFieldError === "missing"
                }
              />
              <input
                value={customerPhone}
                onChange={(event) => {
                  setCustomerPhone(event.target.value);
                  setContactInvalid(false);
                  setContactFieldError(null);
                }}
                placeholder="+79001234567"
                type="tel"
                autoComplete="tel"
                aria-invalid={
                  contactFieldError === "phone" ||
                  contactFieldError === "missing_phone" ||
                  contactFieldError === "missing"
                }
              />
            </div>
          ) : null}

          {isAuthenticated ? (
            <>
              <p className="trust">После оплаты доступ активируется автоматически.</p>
              <p className="legal">
                Нажимая кнопку оплаты, вы соглашаетесь с{" "}
                <a href="/oferta.html">офертой</a>,{" "}
                <a href="/terms.html">условиями использования</a>,{" "}
                <a href="/privacy.html">политикой конфиденциальности</a> и{" "}
                <a href="/consent.html">согласием на обработку персональных данных</a>.
              </p>
            </>
          ) : null}
          {plans.isError ? (
            <p className="notice">
              Не удалось подтянуть тариф из API. Обновите страницу перед оплатой.
            </p>
          ) : null}
          {checkoutErr ? <p className="error-text">{checkoutErr}</p> : null}
        </div>
      </aside>
    </main>
  );
}
